import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastPosition } from '@this+that/ui';
import log from 'loglevel';

import { useDeleteInboxEntry } from './useDeleteInboxEntry';
import { useMarkInboxEntryAsResolved } from './useMarkInboxEntryAsResolved';
import { useMarkInboxEntryAsUnresolved } from './useMarkInboxEntryAsUnresolved';
import { UndoToast } from '../../components/UndoToast';
import { useInboxEntriesStore } from '../../store';
import {
  PendingAction,
  PendingActionType,
  usePendingActionsStore,
} from '../../store/PendingActions';
import { pushGTMEvent } from '../../utils/analytics/gtm';
import { UNDO_WINDOW_MS } from '../../utils/constants/pendingActions';
import { useCustomToast } from '../useCustomToast';

interface ScheduleActionParams {
  type: PendingActionType;
  entryId: string;
  entrySubject: string;
}

export function useDeferredInboxEntryAction() {
  const { t } = useTranslation();
  const { customToaster, errorMessage, closeAllToasts } = useCustomToast();
  const { deleteInboxEntry } = useDeleteInboxEntry();
  const { markAsResolved } = useMarkInboxEntryAsResolved();
  const { markAsUnresolved } = useMarkInboxEntryAsUnresolved();

  const addPendingAction = usePendingActionsStore((s) => s.addPendingAction);
  const removePendingAction = usePendingActionsStore(
    (s) => s.removePendingAction,
  );
  const commitPendingAction = usePendingActionsStore(
    (s) => s.commitPendingAction,
  );
  const getPendingActionForEntry = usePendingActionsStore(
    (s) => s.getPendingActionForEntry,
  );
  const clearAllPendingActions = usePendingActionsStore(
    (s) => s.clearAllPendingActions,
  );

  const commitActionRef = useRef<
    ((actionId: string) => Promise<void>) | undefined
  >(undefined);

  const getToastMessage = useCallback(
    (type: PendingActionType, subject: string): string => {
      switch (type) {
        case PendingActionType.DELETE:
          return t('doBox.undoToast.deletedSingle', { subject });
        case PendingActionType.RESOLVE:
          return t('doBox.undoToast.resolvedSingle', { subject });
        case PendingActionType.UNRESOLVE:
          return t('doBox.undoToast.unresolvedSingle', { subject });
        default:
          log.warn(`Unhandled pending action type: ${type}`);
          return '';
      }
    },
    [t],
  );

  const executeAction = useCallback(
    async (action: PendingAction): Promise<boolean> => {
      switch (action.type) {
        case PendingActionType.DELETE: {
          const success = await deleteInboxEntry(action.entryId);
          if (success) {
            pushGTMEvent({ event: 'dobox_deleted' });
          }
          return success;
        }
        case PendingActionType.RESOLVE: {
          return await markAsResolved(action.entryId);
        }
        case PendingActionType.UNRESOLVE: {
          return await markAsUnresolved(action.entryId);
        }
        default:
          return true;
      }
    },
    [deleteInboxEntry, markAsResolved, markAsUnresolved],
  );

  const executeAndHandleAction = useCallback(
    async (action: PendingAction): Promise<void> => {
      const shouldForceRemoveFromStore =
        action.type === PendingActionType.DELETE;
      let succeeded = false;
      try {
        succeeded = await executeAction(action);
        if (!succeeded) {
          errorMessage(t('doBox.undoToast.commitError'));
        }
      } catch (error) {
        log.error('Failed to execute deferred action', error);
        errorMessage(t('doBox.undoToast.commitError'));
      } finally {
        if (shouldForceRemoveFromStore && succeeded) {
          const storeGroup =
            useInboxEntriesStore.getState().taskManager.activeTaskGroup;
          const entryStillInStore = storeGroup.inboxEntries.some(
            (e) => e.id === action.entryId,
          );
          if (entryStillInStore) {
            await storeGroup.removeInboxEntry(action.entryId);
          }
        }
      }
    },
    [executeAction, errorMessage, t],
  );

  const commitAction = useCallback(
    async (actionId: string) => {
      const action = usePendingActionsStore
        .getState()
        .pendingActions.get(actionId);
      if (!action) {
        return;
      }
      await executeAndHandleAction(action);
      commitPendingAction(actionId);
    },
    [executeAndHandleAction, commitPendingAction],
  );

  useEffect(() => {
    commitActionRef.current = commitAction;
  }, [commitAction]);

  const cancelAction = useCallback(
    (actionId: string) => {
      removePendingAction(actionId);
    },
    [removePendingAction],
  );

  const scheduleAction = useCallback(
    ({ type, entryId, entrySubject }: ScheduleActionParams) => {
      const existingAction = getPendingActionForEntry(entryId);
      if (existingAction) {
        removePendingAction(existingAction.id);
      }

      const actionId = `${type}-${entryId}-${Date.now()}`;

      const timerId = setTimeout(() => {
        commitActionRef.current?.(actionId);
      }, UNDO_WINDOW_MS);

      const pendingAction: PendingAction = {
        id: actionId,
        type,
        entryId,
        createdAt: Date.now(),
        timerId,
      };

      addPendingAction(pendingAction);

      const message = getToastMessage(type, entrySubject);

      customToaster(
        <UndoToast message={message} onUndo={() => cancelAction(actionId)} />,
        ToastPosition.BottomLeft,
        UNDO_WINDOW_MS + 500,
      );
    },
    [
      addPendingAction,
      removePendingAction,
      getPendingActionForEntry,
      getToastMessage,
      customToaster,
      cancelAction,
    ],
  );

  const flushAllPendingActions = useCallback(async () => {
    closeAllToasts();
    const actions = clearAllPendingActions();
    const commitPromises: Promise<void>[] = [];

    for (const action of actions.values()) {
      const commit = async () => {
        await executeAndHandleAction(action);
      };
      commitPromises.push(commit());
    }

    await Promise.allSettled(commitPromises);
  }, [closeAllToasts, clearAllPendingActions, executeAndHandleAction]);

  return {
    scheduleAction,
    cancelAction,
    flushAllPendingActions,
  };
}
